import { boundToOneShop } from '@croissants/contracts/projections';
import { Spinner, usePlatformNow, useQueryState } from '@croissants/ui';
import type { JSX, ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { useShopFacts, useWindows } from '../hooks/queries';
import { compactDays } from '../lib/compact-days';
import { earliestArrivalMinutes, orderNowMinutesOf } from '../lib/delivery-choice';
import { clockTime, relativeServiceDay, serviceDayLabel, windowRange } from '../lib/format';
import { deliveryChoiceHref, deliveryChoiceOffered, pickerThen } from '../lib/journey';
import { chosenMornings, isSoonestChoice } from '../lib/stay';
import { resolvePromisedWindow } from '../lib/windows';
import { useBasket } from '../state/basket-provider';
import { useCheckoutDraft } from '../state/checkout';
import { useLocale } from '../state/locale';
import { useSession } from '../state/session';

/** The screens a delivery time is being read against, and may be changed from. */
const SHOWN_ON = ['/menu', '/basket'] as const;

/**
 * THE REVIEW STEP, FOR EVERY MODE (founder, 2026-09-21): "on the checkout page,
 * keep the delivery date/time detail on the top". The review no longer lists
 * the mornings, so this line is where a guest reads them before paying - in
 * mode A and mode B1 too, which state their morning ON the shelf and the basket
 * (`ChosenMorning`) and so draw this line nowhere else.
 */
const SHOWN_FOR_ALL_ON = '/checkout';

/**
 * MODE B2: the delivery time the guest chose, as a third line of the band under
 * the brand bar - below "Delivering to" and below the free-delivery basket
 * (founder decision, 2026-09-21; PRD GST-045).
 *
 * It is `ChosenMorning`'s statement in one line, in the place a B2 guest reads
 * the other two facts about ordering here, and that block is not drawn for this
 * mode - one statement of the time, never two. "Change" raises the delivery
 * popup again, on the screen it was pressed on (founder, 2026-09-21) - the same
 * two sections the guest first answered, so now and later stay one control. It
 * falls back to the picker only where there is no popup: pre-order alone
 * (`deliveryChoiceOffered`). The basket is untouched either way, because
 * nothing is written until the guest chooses.
 *
 * An order for NOW reads "Delivery in about 35 min", from the
 * picker's own Soonest offer (`earliestArrivalMinutes`) - an earliest, never a
 * range: the range is named once, on the review screen, from the quote
 * (RTE-034). A time the guest named reads as the day and its half-hour.
 *
 * no-eternal-spinner: reads the picker's cache and degrades to words without a
 * figure, never to a spinner.
 */
export function DeliveryTime(): JSX.Element | null {
  const { locale, t } = useLocale();
  const { session } = useSession();
  const { basket } = useBasket();
  const { draft } = useCheckoutDraft();
  const [location] = useLocation();

  const { serviceDay, windowStart } = draft;
  const bound = session !== undefined && session !== null && boundToOneShop(session.storefrontMode);
  const shown =
    session !== undefined &&
    session !== null &&
    serviceDay !== null &&
    windowStart !== null &&
    (location.startsWith(SHOWN_FOR_ALL_ON) ||
      (bound && SHOWN_ON.some((path) => location.startsWith(path))));
  /** Same key the picker asks under, so this is a cache read on the way past. */
  const windows = useWindows(basket, null, shown);
  const platformNow = usePlatformNow(windows.data?.serverTime ?? null, 60_000);
  /* The figure is the platform's (`orderNowMinutes`), asked only while the
     line is drawn for an order for NOW - the same read the popup made, so
     normally a cache hit. */
  /* ...and only of a session bound to one shop: the server refuses this read
     for every other mode, which has no such shop to state a figure for. */
  const factsAsked = shown && bound && draft.soonest === true;
  const facts = useShopFacts(factsAsked);
  /* The shared phase, so a parked or stalled read settles to the words rather
     than holding the wheel (TST-001). A read not asked is `pending` here by
     definition, which is why the wheel below is gated on `factsAsked` too. */
  const factsPhase = useQueryState(facts, { disabled: !factsAsked });

  if (!shown || serviceDay === null || windowStart === null) return null;

  const timeZone = windows.data?.timeZone ?? session?.timeZone ?? 'Europe/Paris';
  let words: ReactNode;
  /*
   * WHICH row the guest chose is the draft's own record (`soonest`, written by
   * "Order now" and by the picker), and it is asked first. Matching the held
   * instant against the answer's Soonest row is not enough on its own: that
   * row's start moves with the clock, so a few minutes after "Order now" the
   * instants no longer agree and this line fell back to "from 06:48" - a start
   * time for an order that has none (seen in Chrome, 2026-09-21).
   */
  if (draft.soonest === true || isSoonestChoice(windows.data, serviceDay, windowStart)) {
    const fromPicker = (): number | null =>
      earliestArrivalMinutes(windows.data?.serviceDays, platformNow);
    /*
     * THE PLATFORM'S FIGURE WHERE IT WAS ASKED, THE PICKER'S EVERYWHERE ELSE.
     * The facts are a B2 read; on the review of mode A or mode B the line is
     * drawn too and that read is never made, so the picker's own derivation
     * from the windows answer is the figure. Until 2026-10-06 the branch
     * below waited on an answer that could not come - Breteuil, ordering from
     * Maison Kayser (mode B), read a wheel after "Delivering to" for ever.
     */
    const minutes = factsAsked ? orderNowMinutesOf(facts.data?.facts, fromPicker) : fromPicker();
    /* The figure is the picker's; while that answer is on its way the line is
       the same words with a loading wheel (founder, 2026-09-21), and only an
       answer that holds no order for now falls back to the plain statement.
       no-eternal-spinner: the wheel is drawn while the read is IN FLIGHT and
       for no other reason - a read never asked, one the server refused, or
       one past the reader's patience, degrades to the words. */
    words =
      minutes !== null ? (
        t('delivery.lineInMinutes', { minutes })
      ) : factsAsked && factsPhase.phase === 'pending' ? (
        /* The wheel alone: the line above already says "Delivering to". */
        <Spinner size={12} label={t('delivery.in')} />
      ) : (
        t('menu.arrivingToday')
      );
  } else {
    const promised = resolvePromisedWindow(undefined, windows.data, serviceDay, windowStart);
    const range =
      promised === null
        ? t('checkout.windowFrom', { time: clockTime(windowStart, locale, timeZone) })
        : windowRange(promised, locale);
    /* One window for the whole stay (D1, D2), so several mornings are a count
       and the half-hour they share; the review screen lists them. */
    const days = chosenMornings(draft.selectedDays, serviceDay);
    /*
     * TODAY AND TOMORROW BY NAME (founder, 2026-09-21): a time chosen for later
     * today reads "Today, 12:45-13:15" rather than a date the guest has to work
     * out is today. Which day is today is the platform's clock, never the
     * device's (TST-003) - the same helper the picker's first two cards use.
     * Several mornings are listed in the least space a phone allows
     * (`compactDays`): "22-24, 26 Sept".
     */
    const relative = relativeServiceDay(serviceDay, timeZone, platformNow);
    const day =
      days.length > 1
        ? compactDays(days, locale)
        : relative === 'TODAY'
          ? t('windows.today')
          : relative === 'TOMORROW'
            ? t('windows.tomorrow')
            : serviceDayLabel(serviceDay, locale, timeZone);
    words = t('delivery.chosen', { day, window: range });
  }

  return (
    <span className="gw-appbar__time">
      <span className="gw-appbar__timewords">{words}</span>
      {/* A BUTTON, at the right (founder, 2026-09-21): as a text link in the
          sentence it read as part of it. A router link wearing the shared
          button, because `LinkButton` is a plain anchor and would reload the
          app - and the basket with it. */}
      <Link
        href={
          deliveryChoiceOffered({
            storefrontMode: session?.storefrontMode ?? null,
            orderingModes: session?.orderingModes ?? null,
          })
            ? deliveryChoiceHref(location)
            : pickerThen(location)
        }
        className="cib-button cib-button--secondary cib-button--sm gw-appbar__change"
      >
        <span className="cib-button__label">{t('windows.change')}</span>
      </Link>
    </span>
  );
}
